import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { Sort } from '@angular/material';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/typings/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable, Subject, combineLatest } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { CopyToEmrModalComponent } from 'src/app/copy-to-emr-modal/copy-to-emr-modal.component';
import { QuestionnaireRequestService } from 'src/app/core/api/questionnaire-request.service';
import { NavigationService } from 'src/app/core/api/navigation.service';
import { patientsDashboardSideNavSchema } from 'src/app/core/schemas/navigation-schema';
import { LocationSelectService } from 'src/app/shared/location-select/location-select.service';
import { HealthCardMode } from 'src/app/shared/location-select/location.model';
import { Patient } from '../../core/api/patient.service';
import { StaffService } from '../../core/api/staff.service';
import { PatientModalComponent } from '../../shared/consult-forms/patient-modal/patient-modal.component';
import { PatientImportWizardModalComponent } from '../patient-import-wizard-modal/patient-import-wizard-modal.component';
import {
  ContactPriority,
  CONTACT_PRIORITY_ORDER,
  EnrichedPatient,
  FOLLOW_UP_WINDOW_DAYS,
  PRIORITY_ICON_MAP
} from './patient-contact-priority.util';
import { PatientsPaginatorService } from './patients-paginator.service';
import {
  getLatestTimelineActivity,
  getPatientAppChipColour
} from '../patient-app-timeline.util';
import { AppStateService } from '../../core/services/app-state.service';
import { safeParseJson } from 'src/app/shared/util/helper.util';
import { hasMissingEnrollmentFields } from '../patient-enrollment.util';
import { TranslocoService } from '@ngneat/transloco';

export type DashboardFilter =
  | 'all'
  | 'contactNow'
  | 'newQuestionnaire'
  | 'followUpDue'
  | 'noQuestionnaire'
  | 'upToDate';

export interface SummaryStats {
  contactNow: number;
  newQuestionnaire: number;
  followUpDue: number;
  noQuestionnaire: number;
  upToDate: number;
}

/** The contact-priority tier each single-tier filter narrows to. `all` intentionally has no entry. */
const FILTER_TIER: { [filter in DashboardFilter]?: ContactPriority } = {
  contactNow: ContactPriority.URGENT,
  newQuestionnaire: ContactPriority.HIGH,
  followUpDue: ContactPriority.FOLLOW_UP,
  noQuestionnaire: ContactPriority.NEW,
  upToDate: ContactPriority.ROUTINE
};

export interface FilterTab {
  key: DashboardFilter;
  labelKey: string;
  tooltipKey: string;
  icon: string;
  colorClass: string;
  countKey?: keyof SummaryStats;
}

/** Single source of truth for the filter bar — rendered with *ngFor so the markup is not repeated. */
const FILTER_TABS: FilterTab[] = [
  { key: 'all', labelKey: 'All', tooltipKey: 'legend-ALL', icon: 'people', colorClass: '' },
  { key: 'contactNow', labelKey: 'Contact Now', tooltipKey: 'legend-URGENT', icon: 'warning', colorClass: 'filter-tab-urgent', countKey: 'contactNow' },
  { key: 'newQuestionnaire', labelKey: 'New Questionnaire', tooltipKey: 'legend-HIGH', icon: 'assignment_turned_in', colorClass: 'filter-tab-high', countKey: 'newQuestionnaire' },
  { key: 'followUpDue', labelKey: 'Follow-Up Due', tooltipKey: 'legend-FOLLOW_UP', icon: 'schedule', colorClass: 'filter-tab-followup', countKey: 'followUpDue' },
  { key: 'noQuestionnaire', labelKey: 'No Questionnaire', tooltipKey: 'legend-NEW', icon: 'help_outline', colorClass: 'filter-tab-new', countKey: 'noQuestionnaire' },
  { key: 'upToDate', labelKey: 'ROUTINE', tooltipKey: 'legend-ROUTINE', icon: 'check_circle', colorClass: 'filter-tab-routine', countKey: 'upToDate' }
];

export interface LegendItem {
  chipClass: string;
  icon: string;
  labelKey: string;
  descKey: string;
}

const LEGEND_ITEMS: LegendItem[] = FILTER_TABS.filter(tab => !!FILTER_TIER[tab.key]).map(tab => ({
  chipClass: 'priority-chip-' + FILTER_TIER[tab.key],
  icon: tab.icon,
  labelKey: tab.labelKey,
  descKey: tab.tooltipKey
}));

@Component({
  selector: 'csi-patients-dashboard',
  templateUrl: './patients-dashboard.component.html',
  styleUrls: ['./patients-dashboard.component.scss'],
  providers: [PatientsPaginatorService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PatientsDashboardComponent implements OnInit, OnDestroy {
  @Input() forEreferral: boolean;

  public patients$: Subject<Partial<Patient>[]> = new Subject<Partial<Patient>[]>();
  public totalNumberOfPatients: number;
  public hasMorePatientsToLoad: boolean;
  public isLoading = true;
  public readonly lastAssessmentKey = 'lastAssessment';
  public readonly translocoFileKey = 'dashboard.patients-dashboard.';

  public isDragging = false;
  public readonly HealthCardMode = HealthCardMode;
  public activeFilter: DashboardFilter = 'all';
  public readonly filterTabs = FILTER_TABS;
  public readonly legendItems = LEGEND_ITEMS;
  public allEnrichedPatients: EnrichedPatient[] = [];
  public currentPageIndex = 0;
  private currentSort: Sort = null;
  public summaryStats: SummaryStats = {
    contactNow: 0,
    newQuestionnaire: 0,
    followUpDue: 0,
    noQuestionnaire: 0,
    upToDate: 0
  };
  public showLegend = false;

  public readonly columnsToDisplay$: Observable<string[]> = combineLatest([
    this.locationSelectService.getHealthCardConfigForClinic(),
    this.appStateService.isPatientAppActive$
  ]).pipe(
    map(([healthCardMode, isPatientAppActive]) => {
      const columns =
        healthCardMode === this.HealthCardMode.noHealthCard
          ? ['priority', 'firstName', 'lastName', 'birthday', 'email', 'phone', 'lastEncounter', 'lastQuestionnaire']
          : ['priority', 'firstName', 'lastName', 'birthday', 'healthCard', 'email', 'phone', 'lastEncounter', 'lastQuestionnaire'];
      if (isPatientAppActive) {
        columns.push('patientApp');
      }
      return columns;
    })
  );

  public readonly pageChange: Subject<PageEvent> = new Subject<PageEvent>();
  public readonly patientsPerPage = 50;

  constructor(
    private route: ActivatedRoute,
    private dialog: MatDialog,
    public router: Router,
    private staffService: StaffService,
    private patientsPaginatorService: PatientsPaginatorService,
    private changeDetectorRef: ChangeDetectorRef,
    private locationSelectService: LocationSelectService,
    private matDialogService: MatDialog,
    public navigationService: NavigationService,
    private questionnaireRequestService: QuestionnaireRequestService,
    private appStateService: AppStateService,
    private translocoService: TranslocoService
  ) {
    this.patientsPaginatorService.setPageSize(this.patientsPerPage);
  }

  ngOnInit() {
    this.navigationService.updateSideNavSchema(patientsDashboardSideNavSchema);
    this.pageChange
      .pipe(debounceTime(100), untilDestroyed(this))
      .subscribe(currentPage => this.changePage(currentPage.pageIndex));
    this.pageChange.next({ pageIndex: 0, length: null, pageSize: null, previousPageIndex: null });

    this.translocoService.langChanges$.pipe(untilDestroyed(this)).subscribe(() => {
      const allPatients = this.patientsPaginatorService.allPatients;
      if (allPatients) {
        allPatients.forEach(p => this.decoratePatientApp(p));
      }
    });
  }

  public trackByFilterKey(_index: number, tab: FilterTab): string {
    return tab.key;
  }

  public trackByLegendChip(_index: number, item: LegendItem): string {
    return item.chipClass;
  }

  ngOnDestroy() {}

  public createPatient() {
    this.staffService.getLoggedInStaff().subscribe(loggedInDoctor => {
      PatientModalComponent.open(this.dialog, loggedInDoctor);
    });
  }

  public openPatientPage(patient: Patient) {
    this.forEreferral
      ? this.router.navigateByUrl(`/refer/patient/${patient.id}`)
      : this.router.navigate([`./${patient.id}`], { relativeTo: this.route });
  }

  public changePage(pageIndex: number) {
    this.currentPageIndex = pageIndex;
    if (this.allEnrichedPatients.length > 0) {
      this.showPage();
      return;
    }
    this.isLoading = true;
    this.changeDetectorRef.detectChanges();
    this.patientsPaginatorService
      .loadNextPatients(0)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.allEnrichedPatients = this.patientsPaginatorService.allPatients;
        this.computeSummaryStats(this.allEnrichedPatients);
        this.decoratePatients(this.allEnrichedPatients);
        this.isLoading = false;
        this.showPage();
      });
  }

  public applyFilter(filterKey: DashboardFilter) {
    this.activeFilter = filterKey;
    this.currentPageIndex = 0;
    this.showPage();
  }

  private showPage() {
    const sorted = this.sortPatients(this.getFilteredPatients(this.allEnrichedPatients, this.activeFilter));
    this.totalNumberOfPatients = sorted.length;
    const start = this.currentPageIndex * this.patientsPerPage;
    this.patients$.next(sorted.slice(start, start + this.patientsPerPage));
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Derives each patient's display data once, on load. Kept out of the sort/filter path so
   * reordering the table doesn't re-derive the whole list. The tooltip is stored as a key +
   * params and translated by the `transloco` pipe in the template, so it stays language-reactive.
   */
  private decoratePatients(patients: EnrichedPatient[]) {
    patients.forEach(patient => {
      const tooltip = this.getPriorityTooltip(patient);
      patient.priorityIcon = PRIORITY_ICON_MAP[patient.contactPriority] || 'person';
      patient.tooltipKey = tooltip.key;
      patient.tooltipParams = tooltip.params;
      patient.qrFriendlyName = this.getQRFriendlyName(patient.latestQuestionnaire);
      this.decoratePatientApp(patient);
    });
  }

  /** Parses the Patient App enrollment config into the status/date fields the table reads. */
  private decoratePatientApp(patient: any) {
    patient.patientAppEnrollmentConfig = safeParseJson(patient.patientAppEnrollmentConfig);
    const enrollmentConfig = patient.patientAppEnrollmentConfig;
    const status = enrollmentConfig ? enrollmentConfig.status : null;
    patient.patientAppStatusChipColour = getPatientAppChipColour(status);

    const enrollmentStatus = hasMissingEnrollmentFields(patient, this.translocoService);
    patient.isMissingEnrollmentFields = enrollmentStatus.isMissingFields;
    patient.missingEnrollmentFieldsMessage = enrollmentStatus.message;

    const timeline = enrollmentConfig ? enrollmentConfig.timeline : null;
    patient.patientAppEnrollmentDate = this.getEnrollmentDate(timeline);
    const latestActivity = this.getLatestActivity(timeline);
    patient.patientAppLastActivityDate = latestActivity ? latestActivity.timestamp : null;
  }

  private getFilteredPatients(patients: EnrichedPatient[], filter: DashboardFilter): EnrichedPatient[] {
    const tier = FILTER_TIER[filter];
    return tier ? patients.filter(patient => patient.contactPriority === tier) : patients;
  }

  private sortByPriority(patients: EnrichedPatient[]): EnrichedPatient[] {
    return patients.slice().sort((first, second) => {
      const firstRank = CONTACT_PRIORITY_ORDER[first.contactPriority] !== undefined
        ? CONTACT_PRIORITY_ORDER[first.contactPriority]
        : 99;
      const secondRank = CONTACT_PRIORITY_ORDER[second.contactPriority] !== undefined
        ? CONTACT_PRIORITY_ORDER[second.contactPriority]
        : 99;
      if (firstRank !== secondRank) { return firstRank - secondRank; }
      const firstDaysSinceAssessment = first.daysSinceAssessment !== null ? first.daysSinceAssessment : 99999;
      const secondDaysSinceAssessment = second.daysSinceAssessment !== null ? second.daysSinceAssessment : 99999;
      return secondDaysSinceAssessment - firstDaysSinceAssessment;
    });
  }

  private computeSummaryStats(patients: EnrichedPatient[]) {
    this.summaryStats = {
      contactNow: this.getFilteredPatients(patients, 'contactNow').length,
      newQuestionnaire: this.getFilteredPatients(patients, 'newQuestionnaire').length,
      followUpDue: this.getFilteredPatients(patients, 'followUpDue').length,
      noQuestionnaire: this.getFilteredPatients(patients, 'noQuestionnaire').length,
      upToDate: this.getFilteredPatients(patients, 'upToDate').length
    };
  }

  private getPriorityTooltip(
    patient: EnrichedPatient
  ): { key: string; params: { [param: string]: number | null } } {
    const key = this.translocoFileKey + 'tooltip-' + patient.contactPriority;
    const daysQR = patient.daysSinceQR;
    const daysAssessment = patient.daysSinceAssessment;

    switch (patient.contactPriority) {
      case ContactPriority.URGENT:
        return { key, params: { days: daysQR } };
      case ContactPriority.HIGH:
        return {
          key,
          params: {
            days: daysQR,
            remaining: daysQR !== null ? Math.max(0, FOLLOW_UP_WINDOW_DAYS - daysQR) : 0
          }
        };
      case ContactPriority.FOLLOW_UP:
      case ContactPriority.ROUTINE:
        return { key, params: { days: daysAssessment } };
      case ContactPriority.NEW:
        return { key, params: {} };
      default:
        return { key, params: {} };
    }
  }

  public getQRFriendlyName(questionnaire: { type: string | null; config: string | null } | null): string {
    if (!questionnaire) { return ''; }
    const name = this.questionnaireRequestService.getQuestionnaireFriendlyName({
      type: questionnaire.type,
      config: questionnaire.config
    });
    return name || questionnaire.type || 'Questionnaire';
  }

  public openPatientImportWizard(file?: File) {
    PatientImportWizardModalComponent.open(this.matDialogService, file);
  }

  public openCopyModal() {
    this.dialog.open(CopyToEmrModalComponent, {
      data: {
        showAssessmentList: true
      },
      width: '95vw',
      minWidth: '67rem',
      height: '92vh'
    });
  }

  public addFile(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
    if (
      event.dataTransfer &&
      event.dataTransfer.files.length !== 0 &&
      event.dataTransfer.files.item(0).name.endsWith('.csv')
    ) {
      this.openPatientImportWizard(event.dataTransfer.files.item(0));
    }
  }

  public dragover(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  announceSortChange(sort: Sort) {
    this.currentSort = sort.direction ? sort : null;
    this.currentPageIndex = 0;
    this.showPage();
  }

  /** Sorts the whole filtered roster by the active column, falling back to priority order. */
  private sortPatients(patients: EnrichedPatient[]): EnrichedPatient[] {
    const sort = this.currentSort;
    if (!sort || !sort.direction) {
      return this.sortByPriority(patients);
    }
    const isAsc = sort.direction === 'asc';
    return patients.slice().sort((first, second) => {
      switch (sort.active) {
        case 'firstName':
          return this.compare(first.firstName, second.firstName, isAsc);
        case 'lastName':
          return this.compare(first.lastName, second.lastName, isAsc);
        case 'email':
          return this.compare(first.email, second.email, isAsc);
        case 'birthday':
          return this.compare(
            new Date(first.dateOfBirth).getTime() || null,
            new Date(second.dateOfBirth).getTime() || null,
            isAsc
          );
        case 'lastEncounter':
          return this.compare(
            first.daysSinceAssessment !== null ? first.daysSinceAssessment : 99999,
            second.daysSinceAssessment !== null ? second.daysSinceAssessment : 99999,
            !isAsc
          );
        case 'priority':
          return this.compare(
            CONTACT_PRIORITY_ORDER[first.contactPriority] !== undefined
              ? CONTACT_PRIORITY_ORDER[first.contactPriority]
              : 99,
            CONTACT_PRIORITY_ORDER[second.contactPriority] !== undefined
              ? CONTACT_PRIORITY_ORDER[second.contactPriority]
              : 99,
            isAsc
          );
        case 'lastQuestionnaire':
          return this.compare(
            first.latestQuestionnaire ? new Date(first.latestQuestionnaire.completedAt).getTime() : 0,
            second.latestQuestionnaire ? new Date(second.latestQuestionnaire.completedAt).getTime() : 0,
            isAsc
          );
        default:
          return 0;
      }
    });
  }

  compare(first: number | string, second: number | string, isAsc: boolean) {
    return (first < second ? -1 : 1) * (isAsc ? 1 : -1);
  }

  getLatestActivity(timeline: any[]) {
    return getLatestTimelineActivity(timeline);
  }

  getEnrollmentDate(timeline: any[]) {
    if (!timeline || timeline.length === 0) {
      return null;
    }
    const enrollEvent = timeline.find(
      item => item.event && item.event.toLowerCase() === 'enrolled'
    );
    return enrollEvent ? enrollEvent.timestamp : null;
  }
}
